// Client-side helpers for fetching broker_summary CSVs directly from Azure Blob
// Storage (trading-data-v2), using a SAS token issued by the backend gate endpoint
// (GET /api/broker-summary/sas-token). Mirrors the direct-blob pattern used by
// BrokerTransaction.tsx / brokerTransactionBlob.ts and the done_summary bundle
// pattern in doneSummaryBlob.ts, ported from backend/src/routes/broker-summary.ts.

import { api } from '../services/api';
import { blobCacheKey } from './cacheKey';
import { fetchZipEntries, fetchZipEntry } from './zipRangeReader';

const BS_ROOT = 'broker_summary/';

const NUMERIC_FIELDS = new Set([
  'BuyerVol', 'BuyerValue', 'SellerVol', 'SellerValue',
  'NetBuyVol', 'NetBuyValue', 'NetSellVol', 'NetSellValue',
  'BuyerAvg', 'SellerAvg', 'NetBuyerAvg', 'NetSellerAvg',
]);

/** Normalise market string to canonical 'RG' | 'TN' | 'NG' or undefined. */
function normalizeMarket(m?: string): 'RG' | 'TN' | 'NG' | undefined {
  if (!m) return undefined;
  const up = m.toUpperCase();
  if (up === 'RK') return 'RG';
  if (up === 'RG' || up === 'TN' || up === 'NG') return up as 'RG' | 'TN' | 'NG';
  return undefined;
}

const BOARD_SUF_MAP: Record<'RG' | 'TN' | 'NG', string> = { RG: '_rg', TN: '_tn', NG: '_ng' };

/**
 * Derive the 1d COMBOS-style suffix (e.g. "", "_rg", "_f", "_rg_f") from the
 * market/investorType filters, matching the board-then-investor concatenation
 * order used by `COMBOS` in 1d_brokersummary.rs.
 */
function getSuf(market: string, investorType: '' | 'F' | 'D'): string {
  // "sector_RG" style market keys carry the board after the underscore.
  const boardSource = market.toLowerCase().startsWith('sector_')
    ? (market.split('_')[1] ?? '')
    : market;

  const normalized = normalizeMarket(boardSource);
  const boardSuf = normalized ? BOARD_SUF_MAP[normalized] : '';
  const invSuf = investorType === 'F' ? '_f' : investorType === 'D' ? '_d' : '';

  return `${boardSuf}${invSuf}`;
}

/** Determine the broker_summary entity type for a ticker, mirroring resolveFDPaths(). */
function getEntityType(code: string): 'stock' | 'idx' | 'sector' {
  if (code.toUpperCase() === 'IDX') return 'idx';
  if (code.length === 4 && /^[A-Z]{4}$/.test(code)) return 'stock';
  return 'sector';
}

export interface BrokerSummaryBlobPaths {
  /** broker_summary_bundle/{YYYYMMDD}/{stock|idx|sector}/{entity}.zip — preferred source. */
  bundlePath: string;
  /** Entry name inside `bundlePath` for this market/investorType combination. */
  entryName: string;
  /** Legacy per-file CSV path, used as a fallback for dates predating bundling. */
  legacyPath: string;
  /** Secondary legacy fallback (IDX with a specific board, no investor filter). */
  legacyFallbackPath?: string;
}

/**
 * Build the broker_summary bundle/entry/legacy paths under trading-data-v2,
 * mirroring the new 1d_brokersummary.rs bundle layout:
 * - stock -> broker_summary_bundle/{date}/stock/{code}.zip, entry emiten{suf}.csv
 * - idx   -> broker_summary_bundle/{date}/idx.zip,           entry idx{suf}.csv
 * - sector-> broker_summary_bundle/{date}/sector/{code}.zip, entry sector{suf}.csv
 *
 * `legacyPath` points at the pre-bundling per-file CSV (same destination 1d used
 * to write to before bundling) for dates that predate the bundle.
 */
export function getBrokerSummaryBlobPath(
  code: string,
  dateStr: string,
  market: string,
  investorType: '' | 'F' | 'D' = '',
): BrokerSummaryBlobPaths {
  const date = dateStr.includes('-') ? dateStr.replace(/-/g, '') : dateStr;
  const suf = getSuf(market, investorType);
  const entityType = getEntityType(code);

  if (entityType === 'idx') {
    const paths: BrokerSummaryBlobPaths = {
      bundlePath: `broker_summary_bundle/${date}/idx.zip`,
      entryName: `idx${suf}.csv`,
      legacyPath: `${BS_ROOT}broker_summary_idx${suf}/broker_summary_idx${suf}_${date}/IDX.csv`,
    };
    // IDX with a board filter but no investor filter: 1d also (historically) wrote
    // IDX as a regular stock-type entry when IDX appears as a real STK_CODE.
    if (suf !== '' && investorType === '') {
      paths.legacyFallbackPath = `${BS_ROOT}broker_summary${suf}/broker_summary${suf}_${date}/IDX.csv`;
    }
    return paths;
  }

  if (entityType === 'sector') {
    return {
      bundlePath: `broker_summary_bundle/${date}/sector/${code}.zip`,
      entryName: `sector${suf}.csv`,
      legacyPath: `${BS_ROOT}broker_summary_sector${suf}/broker_summary_sector${suf}_${date}/${code}.csv`,
    };
  }

  return {
    bundlePath: `broker_summary_bundle/${date}/stock/${code}.zip`,
    entryName: `emiten${suf}.csv`,
    legacyPath: `${BS_ROOT}broker_summary${suf}/broker_summary${suf}_${date}/${code}.csv`,
  };
}

/**
 * Parse broker_summary CSV data (Azure comma-delimited, header-based column lookup),
 * mirroring parseBrokerCSV() in backend/src/routes/broker-summary.ts. Returns raw
 * header-keyed rows (e.g. BrokerCode, BuyerVol, NetBuyVol, ...).
 */
export function parseBrokerSummaryCSV(csvData: string): Record<string, unknown>[] {
  const lines = csvData.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = (lines[0] ?? '').split(',').map(h => h.trim());
  const rows: Record<string, unknown>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = (lines[i] ?? '').split(',').map(v => v.trim());
    if (values.length !== headers.length) continue;

    const row: Record<string, unknown> = {};
    headers.forEach((h, idx) => {
      const raw = values[idx] ?? '';
      row[h] = NUMERIC_FIELDS.has(h) ? (parseFloat(raw) || 0) : (raw || '');
    });
    rows.push(row);
  }

  return rows;
}

const CSV_CACHE_NAME = 'broker-summary-csv-cache';
const ZIP_FETCH_ATTEMPTS = 2;

/**
 * Fetch a legacy per-file broker_summary CSV blob from Azure (via SAS URL), using
 * the browser Cache API for client-side caching. Returns the CSV text, or null if
 * the blob does not exist (HTTP 404).
 */
async function fetchLegacyCsv(legacyPath: string, blobUrl: string, signal?: AbortSignal): Promise<string | null> {
  try {
    const cache = await caches.open(CSV_CACHE_NAME);
    const cached = await cache.match(blobCacheKey(legacyPath));
    if (cached) {
      return cached.text();
    }

    const response = await fetch(blobUrl, signal ? { signal } : undefined);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      throw new Error(`HTTP ${response.status}: ${errText.substring(0, 200)}`);
    }

    const text = await response.text();
    await cache.put(blobCacheKey(legacyPath), new Response(text, { headers: { 'Content-Type': 'text/csv' } }));
    return text;
  } catch (err: any) {
    if (err.name === 'AbortError') throw err;
    if (err.message?.startsWith('HTTP ')) throw err;
    // Cache API unavailable - fallback to direct fetch
    const response = await fetch(blobUrl, signal ? { signal } : undefined);
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`HTTP ${response.status}: ${legacyPath} not found`);
    return response.text();
  }
}

/**
 * Fetch a broker_summary CSV, preferring the per-entity ZIP bundle
 * (broker_summary_bundle/{date}/{stock|idx|sector}/{entity}.zip) via range reads.
 * If the bundle entry can't be read after a couple of attempts, falls back to the
 * legacy per-file CSV (and a secondary legacy fallback for IDX, if present).
 * Returns null if no source has the data.
 */
export async function fetchBrokerSummaryCsv(
  paths: BrokerSummaryBlobPaths,
  containerUrl: string,
  sasToken: string,
  signal?: AbortSignal,
): Promise<string | null> {
  const isDev = (import.meta as any).env?.DEV || false;
  const buildUrl = (path: string) => isDev
    ? `/azure-blob-proxy/trading-data-v2/${path}?${sasToken}`
    : `${containerUrl}/${path}?${sasToken}`;

  const zipUrl = buildUrl(paths.bundlePath);
  for (let attempt = 1; attempt <= ZIP_FETCH_ATTEMPTS; attempt++) {
    try {
      const data = await fetchZipEntry(zipUrl, paths.entryName, signal);
      if (data !== null) return new TextDecoder().decode(data);
    } catch (err: any) {
      if (err?.name === 'AbortError') throw err;
    }
  }

  const legacy = await fetchLegacyCsv(paths.legacyPath, buildUrl(paths.legacyPath), signal);
  if (legacy !== null) return legacy;

  if (paths.legacyFallbackPath) {
    return fetchLegacyCsv(paths.legacyFallbackPath, buildUrl(paths.legacyFallbackPath), signal);
  }

  return null;
}

/**
 * Fetch the main + foreign broker_summary CSVs for one entity with a SINGLE
 * ZIP download (both entries live in the same per-entity archive — e.g.
 * emiten.csv + emiten_f.csv inside stock/{code}.zip). Falls back to two
 * individual fetchBrokerSummaryCsv calls when the bundle entry can't be read.
 */
export async function fetchBrokerSummaryCsvPair(
  paths: BrokerSummaryBlobPaths,
  pathsForeign: BrokerSummaryBlobPaths,
  containerUrl: string,
  sasToken: string,
  signal?: AbortSignal,
): Promise<{ main: string | null; foreign: string | null }> {
  const isDev = (import.meta as any).env?.DEV || false;
  const buildUrl = (path: string) => isDev
    ? `/azure-blob-proxy/trading-data-v2/${path}?${sasToken}`
    : `${containerUrl}/${path}?${sasToken}`;

  const decode = (d: Uint8Array | null | undefined): string | null =>
    d === null || d === undefined ? null : new TextDecoder().decode(d);

  const zipUrl = buildUrl(paths.bundlePath);
  for (let attempt = 1; attempt <= ZIP_FETCH_ATTEMPTS; attempt++) {
    try {
      const entries = await fetchZipEntries(zipUrl, [paths.entryName, pathsForeign.entryName], signal);
      if (entries !== null) {
        return {
          main: decode(entries.get(paths.entryName)),
          foreign: decode(entries.get(pathsForeign.entryName)),
        };
      }
      break; // archive missing entirely → straight to legacy fallback
    } catch (err: any) {
      if (err?.name === 'AbortError') throw err;
    }
  }

  const [main, foreign] = await Promise.all([
    fetchBrokerSummaryCsv(paths, containerUrl, sasToken, signal),
    fetchBrokerSummaryCsv(pathsForeign, containerUrl, sasToken, signal),
  ]);
  return { main, foreign };
}

export interface BrokerSummaryManifest {
  combos: Record<string, { stocks: string[]; sectors: string[] }>;
}

const manifestCache = new Map<string, BrokerSummaryManifest | null>();
const manifestPromises = new Map<string, Promise<BrokerSummaryManifest | null>>();

/**
 * Fetch broker_summary_bundle/{date}/manifest.json directly from Azure Blob,
 * mirroring readBrokerSummaryManifest() in backend/src/routes/broker-summary.ts.
 * Returns null if the manifest doesn't exist (pre-bundling dates).
 */
export async function fetchBrokerSummaryManifest(dateStr: string): Promise<BrokerSummaryManifest | null> {
  const date = dateStr.includes('-') ? dateStr.replace(/-/g, '') : dateStr;

  if (manifestCache.has(date)) {
    return manifestCache.get(date) ?? null;
  }

  let promise = manifestPromises.get(date);
  if (!promise) {
    promise = (async (): Promise<BrokerSummaryManifest | null> => {
      const tokenRes = await api.getBrokerSummarySasToken();
      if (!tokenRes.success || !tokenRes.data) return null;
      const { containerUrl, sasToken } = tokenRes.data;

      const path = `broker_summary_bundle/${date}/manifest.json`;
      const isDev = (import.meta as any).env?.DEV || false;
      const url = isDev
        ? `/azure-blob-proxy/trading-data-v2/${path}?${sasToken}`
        : `${containerUrl}/${path}?${sasToken}`;

      const response = await fetch(url);
      if (!response.ok) return null;
      return await response.json() as BrokerSummaryManifest;
    })().catch(() => null);
    manifestPromises.set(date, promise);
  }

  const manifest = await promise;
  manifestCache.set(date, manifest);
  manifestPromises.delete(date);
  return manifest;
}

/**
 * List available tickers (stocks + sectors) for a date, mirroring the manifest
 * fast-path of GET /api/broker-summary/stocks. Returns null if no manifest is
 * available for this date (e.g. pre-bundling dates) — callers should fall back
 * to a static stock list in that case.
 */
export async function fetchAvailableStocks(
  dateStr: string,
  market: 'RG' | 'TN' | 'NG' | '' = 'RG',
  investorType: '' | 'F' | 'D' = '',
): Promise<{ stocks: string[]; sectors: string[] } | null> {
  const manifest = await fetchBrokerSummaryManifest(dateStr);
  const suf = getSuf(market, investorType);
  const combo = manifest?.combos?.[suf];
  if (!combo) return null;

  const stocks = Array.from(new Set([...combo.stocks, 'IDX'])).sort();
  const idxPos = stocks.indexOf('IDX');
  if (idxPos > 0) {
    stocks.splice(idxPos, 1);
    stocks.unshift('IDX');
  }

  return { stocks, sectors: [...combo.sectors].sort() };
}
